import React, { useRef, useState, useEffect } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, Tooltip, TooltipProps, ReferenceLine } from "recharts";
import { ValueType, NameType } from "recharts/types/component/DefaultTooltipContent";
import { FaRegFilePdf } from "react-icons/fa6";
import { TbFileExcel } from "react-icons/tb";
import { FaRegCopy } from "react-icons/fa";
import { BsFiletypeCsv } from "react-icons/bs";
import { exportToExcel } from "./ExportUtils";
import { copyChartToClipboard } from "./ExcelCurved";
import { exportToPDF, exportToCSV } from "./ExportUtils";
import { useAssetContext } from "../../DataContext/AssetContext";
import { FaSearchMinus } from "react-icons/fa";
import { useDevice } from "./Device-Responsive";
import { DateTime } from "luxon";

interface ChartProps {
  chartData: Array<any>;
  metrics: Array<string>;
  xAxis: string;
  legend: Array<string>;
  xAxisLabel: string;
  yAxisLabel: { value: string; dy: number; offset: number};
  selectedFrequency: string;
  selectedDateRange: string;
  useIntervalAggregation: boolean;
  lineMetric: string;
  lineMetricName: string;
  lineColor: string;
  rightYAxisLabel?: string;
}

const predefinedColors = ["#F6866A", "#59E6F6", "#8A6CE3", "#FABE7A", "#2C3192", "#7661E2", "#D92626",
  "#26D95A", "#8E26D9", "#D9C226", "#26BBD9", "#D92687", "#53D926", "#2E26D9", "#D96226", "#26D996", "#E03A3A", "#3AE072",
  "#9A3AE0", "#E0D13A", "#3AC3E0", "#E03A95", "#65E03A", "#403AE0", "#E07B3A", "#3AE0C9"];

const generateUniqueColors = (count: number) =>
  Array.from(
    { length: count },
    (_, i) => `hsl(${(i * 137.5) % 360}, 70%, 50%)`
  );

// ----------------- Color Logic -----------------
// Hardcoded metric colors
const hardcodedColors: Record<string, string> = {
  'DAM': '#F6866A', 
  'Energy': '#F6866A',
  'RTM': '#59E6F6', 
  'Ancillary Services': '#59E6F6', 
  'A/S Regulation Down': '#59E6F6',
  'RTUC': '#8A6CE3',
  'A/S Regulation Up': '#FABE7A',
  'A/S Spinning': '#2C3192',
  'Meter': '#7661E2',
  'RUC Capacity': '#53D926',
  'A/S Non-Spinning': '#E03A95',
};

const assignedColors: Record<string, string> = { ...hardcodedColors };
const GOLDEN_ANGLE = 137.508;

const getSoftBrightColorForMetric = (metric: string, index: number) => {
  if (assignedColors[metric]) return assignedColors[metric];

  const hue = (index * GOLDEN_ANGLE) % 360;
  const saturation = 55 + (index % 15);
  const lightness = 55 + (index % 10);

  const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  assignedColors[metric] = color;
  return color;
};

interface CustomTooltipProps extends TooltipProps<ValueType, NameType> {
  selectedFrequency: string;
  useIntervalAggregation: boolean;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  selectedFrequency,
  useIntervalAggregation,
}) => {
  if (active && payload && payload.length) {
    const filteredPayload = payload.filter(
      (entry) => entry.value !== 0 && entry.value !== null
    );

    if (filteredPayload.length === 0) return null;

    
    const dt = DateTime.fromISO(label, { setZone: true });
    let formattedLabel = "";

    if (useIntervalAggregation) {
      if (["hour", "5minute", "15minute"].includes(selectedFrequency)) {
        formattedLabel = dt.toFormat("hh:mm a");  
      } else {
        formattedLabel = dt.toFormat("MM/dd/yyyy");
      }
    } else {
      if (["day", "week", "month"].includes(selectedFrequency)) {
        formattedLabel = dt.toFormat("MM/dd/yyyy");
      } else {
        formattedLabel = dt.toFormat("MM/dd/yyyy hh:mm a");
      }
    }
const formatNumber = (
  value: any
) =>
  Number(value).toLocaleString(
    undefined,
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );
    return (
      <div className="bg-white shadow-lg rounded-md p-3 border border-gray-300">
        <p className="text-gray-900 font-semibold text-sm mb-1">
          {formattedLabel}
        </p>
        <div className="space-y-1">
          {filteredPayload.map((entry, index) => {
            const color =
              entry.name && hardcodedColors[entry.name]
                ? hardcodedColors[entry.name]
                : entry.color;
            return (
              <div key={`item-${index}`} className="flex items-center">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full mr-2"
                  style={{ backgroundColor: color }}
                ></span>
                <span className="text-xs text-gray-700">
                  {entry.name}:{" "}
                  <span className="font-semibold text-gray-900">
                    {/* {entry.value?.toLocaleString()} */}
                    {formatNumber(entry.value)}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return null;
};


const renderLegend = (props: any, selectedRevenue: string) => {
  const { payload } = props;

  return (
    <div className="flex items-center flex-wrap mb-10">
      <span className="mr-2 font-bold text-xs sm:text-sm capitalize">
        {selectedRevenue}
      </span>
      {payload.map((entry: any, index: number) => {
        const color = hardcodedColors[entry.value] || entry.color;

        return (
          <div key={`item-${index}`} className="flex items-center mr-4">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full mr-2"
              style={{ backgroundColor: color }}
            ></span>
            <span className="text-xs sm:text-sm text-gray-600">
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ChartComponent: React.FC<ChartProps> = ({
  chartData,
  metrics,
  xAxis,
  xAxisLabel,
  yAxisLabel,
  selectedFrequency,
  selectedDateRange,
  useIntervalAggregation,
  lineMetric,
  lineMetricName,
  lineColor,
  rightYAxisLabel,
}) => {
  const panAmount = 1;
  const chartRef = useRef<HTMLDivElement>(null);
  const [fullData, setFullData] = useState(chartData);
  const [displayedData, setDisplayedData] = useState(chartData);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [zoomStack, setZoomStack] = useState<{ start: number; end: number }[]>(
    []
  );
  const [isScrollEnabled, setIsScrollEnabled] = useState(false);
  const handleEnableScroll = () => {
    setIsScrollEnabled(true);
  };

  const { isSmallDevice } = useDevice();
  useEffect(() => {
    setFullData(chartData);
    setDisplayedData(chartData);
    setZoomLevel(1);
  }, [chartData, selectedFrequency, selectedDateRange]);

  const zoomIn = (startIndex: number, endIndex: number) => {
    const MIN_POINTS = 10;
    const ZOOM_FACTOR = 0.5;
    const fullStartIndex = fullData.indexOf(displayedData[0]);
    const fullEndIndex = fullData.indexOf(
      displayedData[displayedData.length - 1]
    );

    startIndex = fullStartIndex + startIndex;
    endIndex = fullStartIndex + endIndex;
    const range = endIndex - startIndex;
    if (range < MIN_POINTS) {
      startIndex = Math.max(0, startIndex - Math.floor(MIN_POINTS / 2));
      endIndex = Math.min(
        fullData.length,
        endIndex + Math.floor(MIN_POINTS / 2)
      );
    }

    const step = Math.max(
      Math.floor((endIndex - startIndex) * ZOOM_FACTOR),
      MIN_POINTS
    );
    const newStart =
      startIndex + Math.floor((endIndex - startIndex - step) / 2);
    const newEnd = newStart + step;
    setZoomStack((prevStack) => [
      ...prevStack,
      { start: fullStartIndex, end: fullEndIndex },
    ]);

    setDisplayedData(fullData.slice(newStart, newEnd));
    setZoomLevel((prevZoom) => prevZoom + 1);
  };

  const zoomOut = () => {
    if (zoomStack.length === 0) {
      setDisplayedData(fullData);
      setZoomLevel(1);
      setIsScrollEnabled(false);
      return;
    }

    const lastZoom = zoomStack[zoomStack.length - 1];
    setDisplayedData(fullData.slice(lastZoom.start, lastZoom.end));

    setZoomStack((prevStack) => prevStack.slice(0, -1));
    setZoomLevel((prevZoom) => prevZoom - 1);
  };
  const panLeft = () => {
    const fullStartIndex = fullData.indexOf(displayedData[0]);
    const newStart = Math.max(0, fullStartIndex - panAmount);
    const newEnd = newStart + displayedData.length;
    setDisplayedData(fullData.slice(newStart, newEnd));
  };
  const panRight = () => {
    const fullStartIndex = fullData.indexOf(displayedData[0]);
    const newEnd = Math.min(
      fullData.length,
      fullStartIndex + displayedData.length + panAmount
    );
    const newStart = newEnd - displayedData.length;
    setDisplayedData(fullData.slice(newStart, newEnd));
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    setDragStartX(event.clientX);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (dragStartX !== null) {
      const diff = event.clientX - dragStartX;
      if (diff > 20) {
        panLeft();
        setDragStartX(event.clientX);
      } else if (diff < -20) {
        panRight();
        setDragStartX(event.clientX);
      }
    }
  };

  const handleMouseUp = () => {
    setDragStartX(null);
  };
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") panLeft();
      if (event.key === "ArrowRight") panRight();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [displayedData, fullData]);

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      if (!isScrollEnabled) return; // Scroll only if enabled

      event.preventDefault();
      const isShiftPressed = event.shiftKey;
      const scrollDirection = event.deltaY;

      if (isShiftPressed) {
        if (scrollDirection > 0) {
          panRight();
        } else {
          panLeft();
        }
      } else {
        if (scrollDirection < 0) {
          zoomIn(0, displayedData.length / 2);
        } else {
          zoomOut();
        }
      }
    };
    const chartElement = chartRef.current;
    if (!chartElement) return;

    chartElement.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      chartElement.removeEventListener("wheel", handleWheel);
    };
  }, [isScrollEnabled, displayedData]);

  const { revenueData, selectedRevenue } = useAssetContext();
  const extraColors = generateUniqueColors(
    Math.max(0, metrics.length - predefinedColors.length)
  );
  const uniqueColors = [...predefinedColors, ...extraColors];

  const getCustomRangeDays = (rangeStr: string): number | null => {
    const match = rangeStr.match(
      /^(\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})$/
    );
    if (!match) return null;

    const [_, startStr, endStr] = match;
    const start = new Date(startStr);
    const end = new Date(endStr);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const generateIntervalMapping = (
    selectedDateRange: string
  ): Record<string, number> => {
    const maxLabels = 31;

    const calcInterval = (totalUnits: number): number => {
      return totalUnits <= maxLabels ? 1 : Math.ceil(totalUnits / maxLabels);
    };

    const getDynamicDayCounts = (range: string): number => {
      const today = new Date();
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

      const msInDay = 1000 * 60 * 60 * 24;

      if (range === "ytd") {
        return (
          Math.floor((today.getTime() - startOfYear.getTime()) / msInDay) + 1
        );
      }

      if (range === "mtd") {
        return (
          Math.floor((today.getTime() - startOfMonth.getTime()) / msInDay) + 1
        );
      }

      return predefinedRanges[range] ?? getCustomRangeDays(range) ?? 0;
    };

    const predefinedRanges: Record<string, number> = {
      minus_7d: 7,
      minus_30d: 30,
      yesterday: 1,
      today: 1,
    };

    const days = getDynamicDayCounts(selectedDateRange);
    const totalDays = days;
    const totalWeeks = Math.floor(days / 7);
    // const totalMonths = Math.max(new Date().getMonth() + 1, 1); // Jan = 0

    const total15MinIntervals = totalDays * 96;
    const total5MinIntervals = totalDays * 288;
    const totalHourIntervals = totalDays * 24;
    const totalHours = totalDays * 24;
    const calcHourAlignedInterval = (pointsPerHour: number): number => {
      const hoursPerTick = Math.max(1, Math.ceil(totalHours / maxLabels));
      return hoursPerTick * pointsPerHour - 1;
    };

    const mapping: Record<string, number> = {
      day: calcInterval(totalDays),
      week: calcInterval(totalWeeks),
      // month: calcInterval(totalMonths),
      "15minute": calcHourAlignedInterval(4),
      "5minute": calcHourAlignedInterval(12),
      hour: calcHourAlignedInterval(1),
    };

    return mapping;
  };

  const intervalMapping = generateIntervalMapping(selectedDateRange);
  const interval = !useIntervalAggregation
    ? intervalMapping[selectedFrequency] ?? 0
    : 0;
  // const leftValues = chartData.map((d) =>
  //   metrics.reduce((sum, m) => sum + (d[m] ?? 0), 0)
  // );
  // const rightValues = chartData.map((d) => d[lineMetric] ?? 0);

  const positiveStacks = chartData.map(d =>
    metrics.reduce((sum, m) => {
      const val = d[m] ?? 0;
      return val > 0 ? sum + val : sum;
    }, 0)
  );

  const negativeStacks = chartData.map(d =>
    metrics.reduce((sum, m) => {
      const val = d[m] ?? 0;
      return val < 0 ? sum + val : sum;
    }, 0)
  );


  const maxPositive = Math.max(...positiveStacks, 0);
  const maxNegative = Math.min(...negativeStacks, 0);


  const padding = 0.10;

  const leftMax = maxPositive * (1 + padding);
  const leftMin = maxNegative * (1 + padding);


  const leftDomain: [number, number] = [
    Math.floor(leftMin),
    Math.ceil(leftMax)
  ];



  const lineValues = chartData.map(d => d[lineMetric] ?? 0);

  const rawMin = Math.min(...lineValues, 0);
  const rawMax = Math.max(...lineValues, 0);

  const spread = Math.abs(rawMax - rawMin);


  const paddingFactor = spread > 0 ? Math.max(1.12, 1 + 8 / spread) : 1.12;

  const rightMin = Math.floor(rawMin * paddingFactor);
  const rightMax = Math.ceil(rawMax * paddingFactor);

  const rightDomain: [number, number] = [rightMin, rightMax];

  // const minLeft = Math.min(...leftValues);
  // const maxLeft = Math.max(...leftValues);
  // const minRight = Math.min(...rightValues);
  // const maxRight = Math.max(...rightValues);

  // const maxAbsLeft = Math.max(Math.abs(minLeft), Math.abs(maxLeft));
  // const maxAbsRight = Math.max(Math.abs(minRight), Math.abs(maxRight));
  // const spreadLeft = Math.abs(maxLeft - minLeft);
  // const spreadRight = Math.abs(maxRight - minRight);
  
  // const leftPaddingFactor = spreadLeft > 0 ? Math.max(1.1, 1 + 10 / spreadLeft) : 1.1;
  // const rightPaddingFactor = spreadRight > 0 ? Math.max(1.1, 1 + 5 / spreadRight) : 1.1;
  
  // const paddedMaxAbsLeft = Math.ceil(maxAbsLeft * leftPaddingFactor * 1.25) + 5;
  // const paddedMaxAbsRight = Math.ceil(maxAbsRight * rightPaddingFactor * 1.25) + 5;
  
  // const leftDomain = [-paddedMaxAbsLeft, paddedMaxAbsLeft];
  // const rightDomain = [-paddedMaxAbsRight, paddedMaxAbsRight];
  

  const generateDynamicTicks = (
    min: number,
    max: number,
    steps: number = 4,
    existingTicks: number[] = []
  ) => {
    if (min === max) return [min]; 

    const range = max - min;
    const rawStep = range / steps;

    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const residual = rawStep / magnitude;
    let niceStep: number;
    if (residual >= 5) niceStep = 5 * magnitude;
    else if (residual >= 2) niceStep = 2 * magnitude;
    else niceStep = magnitude;

    const niceMin = Math.floor(min / niceStep) * niceStep;
    const niceMax = Math.ceil(max / niceStep) * niceStep;

    const customTicks: number[] = [];
    for (let t = niceMin; t <= niceMax; t += niceStep) {

      const decimals = Math.max(0, -Math.floor(Math.log10(niceStep)));
      const rounded = parseFloat(t.toFixed(decimals + 2));
      customTicks.push(rounded);
    }

    
    if (min < 0 && max > 0 && !customTicks.includes(0)) {
      customTicks.push(0);
    }

    const allTicks = [...customTicks, ...existingTicks];

    return [...new Set(allTicks)]
      .filter((t) => t >= min && t <= max) 
      .sort((a, b) => a - b);
  };

  const [stableInterval, setStableInterval] = useState(interval);
  const prevIntervalRef = useRef(interval);

  useEffect(() => {
    if (displayedData.length === 0) return;

    const newInterval =
      zoomLevel === 1
        ? interval === 1
          ? 0
          : interval
        : zoomLevel === 2
        ? Math.ceil(displayedData.length / 30)
        : zoomLevel === 3
        ? Math.ceil(displayedData.length / 45)
        : zoomLevel === 4
        ? Math.ceil(displayedData.length / 60)
        : zoomLevel === 5
        ? Math.ceil(displayedData.length / 75)
        : prevIntervalRef.current;

    const isZoomingIn = newInterval > prevIntervalRef.current;
    prevIntervalRef.current = newInterval;

    if (isZoomingIn) {
      requestAnimationFrame(() => setStableInterval(newInterval));
    } else {
      React.startTransition(() => {
        setStableInterval(newInterval);
      });
    }
  }, [displayedData, zoomLevel]);

  const sortedMetrics = [...metrics].sort((a, b) => {
    const totalA = chartData.reduce((sum, d) => sum + (d[a] ?? 0), 0);
    const totalB = chartData.reduce((sum, d) => sum + (d[b] ?? 0), 0);
    return totalB - totalA;
  });

  return (
    <div className="bg-white p-3 font-poppins relative">
      <div className="flex justify-end space-x-4 sm:space-x-3 relative -top-16 md:-top-14 lg:-top-14">
        <FaRegFilePdf
          className="text-gray-500 hover:text-red-500 text-xl transform hover:scale-110 transition"
          title="Export as PDF"
          onClick={() =>
            exportToPDF(chartRef, chartData, revenueData, "Chart_Report")
          }
        />
        <BsFiletypeCsv
          className="text-gray-500 hover:text-red-500 text-xl transform hover:scale-110 transition"
          title="Export as CSV"
          onClick={() => exportToCSV(chartData, "exported_data")}
        />
        <TbFileExcel
          className="text-gray-500 hover:text-red-500 text-xl transform hover:scale-110 transition"
          title="Export as Excel"
          onClick={() => exportToExcel(chartRef, chartData, revenueData)}
        />
        <FaRegCopy
          className="text-gray-500 hover:text-red-500 text-xl transform hover:scale-110 transition"
          title="Copy Data"
          onClick={() =>
            copyChartToClipboard("chart-container", chartData, revenueData)
          }
        />
      </div>
      <div id="chart-container">
        <div ref={chartRef}>
          <div
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onClick={handleEnableScroll}
          >
            <div className="flex justify-end ">
              {zoomLevel > 1 && (
                <button
                  onClick={zoomOut}
                  className="flex items-center px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-700"
                >
                  <FaSearchMinus className="mr-2" /> Zoom Out
                </button>
              )}
            </div>

            <ResponsiveContainer
              width="100%"
              height={isSmallDevice ? 400 : 500}
            >
              <ComposedChart
                data={zoomLevel > 1 ? displayedData : chartData}
                onClick={(event: any) => {
                  if (!event || !event.activeLabel) return;
                  const clickedIndex = displayedData.findIndex(
                    (d) => d[xAxis] === event.activeLabel
                  );

                  if (clickedIndex !== -1) {
                    // console.log("Clicked Index:", clickedIndex);
                    const rangeSize = Math.floor(displayedData.length / 2);
                    const start = Math.max(
                      0,
                      clickedIndex - Math.floor(rangeSize / 2)
                    );
                    const end = Math.min(
                      displayedData.length,
                      clickedIndex + Math.floor(rangeSize / 2)
                    );

                    zoomIn(start, end);
                  }
                }}
                margin={{
                  top:0,
                  right: isSmallDevice ? 35 : 55,
                  bottom: isSmallDevice ? 80 : 90,
                  left: isSmallDevice ? 38 : 55,
                }}
                stackOffset="sign"
              >
                <CartesianGrid stroke="#f5f5f5" />
                <XAxis
                  dataKey="time"
                  tickFormatter={(value, index) => {
                    const dt = DateTime.fromISO(value, { setZone: true });

                    if (
                      selectedFrequency === "day" &&
                      selectedDateRange === "ytd" &&
                      useIntervalAggregation
                    ) {
                      if (displayedData.length > 90 && index % 7 !== 0) return "";
                      return dt.toFormat("MM/dd/yyyy");
                    }

                    if (["day", "week", "month"].includes(selectedFrequency)) {
                      return dt.toFormat("MM/dd/yyyy");
                    }

                    if (!useIntervalAggregation) {
                      return dt.toFormat("MM/dd/yyyy hh:mm a");
                    }

                    if (["15minute", "5minute", "hour"].includes(selectedFrequency)) {
                      return dt.minute === 0 ? dt.toFormat("hh:mm a") : ""; // <— include AM/PM
                    }

                    return dt.toFormat("MM/dd/yyyy");
                  }}
                  tick={{ fontSize: isSmallDevice ? 12 : 14 }}
                  angle={-30}
                  textAnchor="end"
                  interval={isSmallDevice ? "preserveStartEnd" : stableInterval}
                  tickCount={
                    isSmallDevice
                      ? Math.min(displayedData.length, 8)
                      : Math.min(displayedData.length, 15)
                  }
                  dy={isSmallDevice ? 20 : displayedData.length > 20 ? 10 : 40}
                  label={{
                    value: xAxisLabel,
                    position: "bottom",
                    offset: isSmallDevice ? 70 : 70,
                    fontSize: isSmallDevice ? 12 : 16,
                    fontWeight: "bold",
                  }}
                />

                <YAxis
                  yAxisId="left"
                  allowDataOverflow
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: isSmallDevice ? 12 : 14 }}
                  ticks={generateDynamicTicks(
                    leftDomain[0],
                    leftDomain[1],
                    isSmallDevice ? 3 : 4
                  )}
                  tickFormatter={(value) => {
                    if (Math.abs(value) >= 1_000_000) {
                      return (value / 1_000_000).toFixed(0) + "M"; 
                    } else if (Math.abs(value) >= 1_000) {
                      return (value / 1_000).toFixed(0) + "k";
                    }
                    return value.toString(); 
                  }}
                  label={{
                    ...yAxisLabel,
                    angle: -90,
                    position: "insideLeft",
                    fontSize: isSmallDevice ? 12 : 14,
                    fontWeight: "bold",
                  }}
                  domain={leftDomain}
                />

                {/* Ensure reference line is applied correctly */}
                <ReferenceLine
                  y={0}
                  stroke="lightgrey"
                  strokeDasharray="3 3"
                  yAxisId="left"
                />
                <ReferenceLine
                  y={0}
                  stroke="lightgrey"
                  strokeDasharray="3 3"
                  yAxisId="right"
                />

                <YAxis
                  yAxisId="right"
                  allowDataOverflow
                  orientation="right"
                  ticks={generateDynamicTicks(
                    rightDomain[0],
                    rightDomain[1],
                    isSmallDevice ? 3 : 4
                  )}
                  tickFormatter={(value) => {
                    if (Math.abs(value) >= 1_000_000) {
                      return (value / 1_000_000).toFixed(0) + "M"; // always whole M
                    } else if (Math.abs(value) >= 1_000) {
                      return (value / 1_000).toFixed(0) + "k"; // always whole k
                    }
                    return value.toString(); // small numbers unchanged
                  }}
                  tick={{ fontSize: isSmallDevice ? 12 : 14 }}
                  axisLine={false}
                  tickLine={false}
                  domain={rightDomain}
                  interval="preserveStartEnd"
                  label={{
                    value: rightYAxisLabel,
                    angle: -90,
                    position: "insideRight",
                    fontSize: isSmallDevice ? 12 : 14,
                    offset: -15,
                    dy: -70,
                    fontWeight: "bold",
                  }}
                />

                <Tooltip
                  content={
                    <CustomTooltip
                      selectedFrequency={selectedFrequency}
                      useIntervalAggregation={useIntervalAggregation}
                    />
                  }
                />

                <Legend
                  content={(props) => renderLegend(props, selectedRevenue)}
                  layout="horizontal"
                  verticalAlign="top"
                  align="center"
                  iconType="circle"
                />

                {sortedMetrics.map((metric, i) => (
                  <Bar
                    key={metric}
                    yAxisId="left"
                    dataKey={metric}
                    stackId="a"
                    fill={getSoftBrightColorForMetric(metric, i)}
                    name={metric}
                    barSize={isSmallDevice ? 25 : 50}
                  />
                ))}

                <Line
                  yAxisId="right"
                  type="linear"
                  dataKey={lineMetric}
                  stroke={lineColor}
                  strokeWidth={isSmallDevice ? 2 : 4}
                  dot={false}
                  activeDot={false}
                  name={lineMetricName}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartComponent;
